import { Check } from 'lucide-react';
import { Page, Reveal, MaskedLines, SectionTag } from '../components/motion';
import { CTASection } from '../components/CTASection';
import { COMPANY, TEAM, REPUTATION, IMAGES, CLIENTS } from '../data/content';
import { useSEO } from '../hooks/useSEO';

const PageHero = () => (
  <section className="relative pt-40 pb-20 lg:pt-52 lg:pb-28 border-b border-line overflow-hidden">
    <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
      <Reveal y={14} className="mb-6">
        <p className="text-xs uppercase tracking-[0.3em] text-accent">About EhimoBCE</p>
      </Reveal>
      <MaskedLines
        lines={['Building with purpose.', 'Delivering with precision.']}
        delay={0.1}
        lineClassName="font-display text-3xl sm:text-5xl lg:text-6xl font-semibold uppercase tracking-tight leading-[0.95]"
      />
    </div>
    <span className="absolute -bottom-10 right-0 font-display text-[13vw] leading-none text-outline select-none pointer-events-none whitespace-nowrap" aria-hidden="true">
      EhimoBCE
    </span>
  </section>
);

const Story = () => (
  <section data-testid="about-story" className="bg-paper text-ink">
    <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-24 lg:py-36 grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <SectionTag number="01" label="Company story" light />
        <div className="group border border-paperline bg-white p-3 mt-4">
          <div className="overflow-hidden">
            <img
              src={IMAGES.about}
              alt="Ehimo Building & Civil Engineering project team on site"
              loading="lazy"
              className="img-treated w-full aspect-[4/3] object-cover group-hover:scale-105"
            />
          </div>
        </div>
      </div>
      <div className="lg:col-span-7 flex flex-col justify-center">
        <Reveal className="space-y-6 max-w-2xl">
          <p className="text-xl lg:text-2xl leading-relaxed text-ink">{COMPANY.intro}</p>
          <p className="text-inkmute leading-relaxed">{COMPANY.reach}</p>
          <p className="text-inkmute leading-relaxed">{COMPANY.leadership}</p>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Reveal delay={0.1} className="border border-paperline p-8 bg-white">
            <p className="font-display text-sm text-ink uppercase tracking-wide mb-4 flex items-center gap-3">
              <span className="w-2.5 h-2.5 bg-accent" aria-hidden="true" /> Our Vision
            </p>
            <p className="text-sm text-inkmute leading-relaxed">{COMPANY.vision}</p>
          </Reveal>
          <Reveal delay={0.2} className="border border-paperline p-8 bg-white">
            <p className="font-display text-sm text-ink uppercase tracking-wide mb-4 flex items-center gap-3">
              <span className="w-2.5 h-2.5 bg-accent" aria-hidden="true" /> Our Mission
            </p>
            <p className="text-sm text-inkmute leading-relaxed">{COMPANY.mission}</p>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

const Reputation = () => (
  <section data-testid="about-reputation" className="bg-white text-ink">
    <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-24 lg:py-36">
      <SectionTag number="02" label="Our reputation" light />
      <MaskedLines
        inView
        lines={['What every project', 'is measured against.']}
        lineClassName="font-display text-3xl sm:text-4xl lg:text-6xl font-semibold uppercase tracking-tight leading-[0.98]"
      />
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-paperline border border-paperline">
        {REPUTATION.map((r, i) => (
          <Reveal key={r} delay={i * 0.06} className="bg-paper p-8 flex gap-4">
            <span className="w-7 h-7 bg-accent flex items-center justify-center shrink-0">
              <Check size={14} className="text-ink" />
            </span>
            <p className="text-sm text-ink/80 leading-relaxed">{r}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Team = () => (
  <section data-testid="about-team" className="bg-paper text-ink border-t border-paperline">
    <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-24 lg:py-36">
      <SectionTag number="03" label="Our team" light />
      <MaskedLines
        inView
        lines={['In-depth experience,', 'on every site.']}
        lineClassName="font-display text-3xl sm:text-4xl lg:text-6xl font-semibold uppercase tracking-tight leading-[0.98]"
      />
      <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-paperline border border-paperline">
        {TEAM.map((m, i) => (
          <Reveal key={m.name} delay={i * 0.06} className="bg-white p-8 group hover:bg-paper transition-colors duration-500">
            <div className="w-12 h-12 bg-accent flex items-center justify-center mb-6">
              <span className="font-display text-ink text-lg font-semibold">
                {m.name.replace(/^(Bldr\.|Eng\.)\s*/, '').split(' ').map((w) => w[0]).slice(0, 2).join('')}
              </span>
            </div>
            <h3 className="font-display text-xl font-medium uppercase tracking-tight text-ink">
              {m.name}
            </h3>
            <p className="mt-3 text-xs text-inkmute leading-relaxed">{m.role}</p>
            <p className="mt-2 text-[10px] uppercase tracking-[0.2em] text-ink/60">{m.experience}</p>
          </Reveal>
        ))}
        <Reveal delay={0.3} className="bg-accent p-8 flex flex-col justify-between">
          <p className="font-display text-ink text-2xl font-semibold uppercase tracking-tight leading-tight">
            Registered builders. Certified professionals.
          </p>
          <p className="mt-6 text-xs text-ink/70 uppercase tracking-[0.2em]">CORBON — NIOB</p>
        </Reveal>
      </div>
    </div>
  </section>
);

const ClientStrip = () => (
  <section className="bg-white text-ink border-t border-paperline">
    <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20">
      <SectionTag number="04" label="Our clients" light />
      <div className="flex flex-wrap items-center gap-x-14 gap-y-8">
        {CLIENTS.map((c) => (
          <img
            key={c.name}
            src={c.logo}
            alt={`${c.name} — EhimoBCE client`}
            loading="lazy"
            className="max-h-10 w-auto opacity-70 hover:opacity-100 transition-opacity duration-500"
          />
        ))}
      </div>
    </div>
  </section>
);

export default function About() {
  useSEO('About', 'Ehimo Building & Civil Engineering — a dynamic building and civil engineering construction company in Lagos, Nigeria, led by CORBON-registered builders and NIOB members.');
  return (
    <Page>
      <PageHero />
      <Story />
      <Reputation />
      <Team />
      <ClientStrip />
      <CTASection title={['Build with', 'EhimoBCE.']} />
    </Page>
  );
}
